import React, { useEffect, useMemo, useState } from "react";
import { FiEdit2, FiEye, FiSearch, FiTrash2 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import Table from "../components/Table";
import AddEditUser from "../modal/AddEditUser";
import AreYouSure from "../modal/AreYouSure";
import ViewUser from "../modal/ViewUser";
import RenderToast from "../components/RenderToast";
import PaginationComp from "../components/PaginationComp";
import { deleteUsers as deleteUsersRequest, saveUser as saveUserRequest } from "../services/portalApi";
import { closeModal, openModal, selectModal, selectModalProps } from "../store/uiSlice";
import { removeUsers, selectUsers, upsertUser } from "../store/usersSlice";
import { generateUserId, normalizeIframeMarkup, normalizeUser, resolveUserSlug } from "../utils/users";
import styles from "./AdminUsersPage.module.css";

const PER_PAGE = 10;

export default function AdminUsersPage() {
  const users = useSelector(selectUsers);
  const { booting = false } = useOutletContext() || {};
  const safeUsers = useMemo(() => users.filter(Boolean), [users]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const modalProps = useSelector(selectModalProps);

  const filteredUsers = useMemo(() => {
    return safeUsers.filter((user) => {
      const searchable = [
        user.id,
        user.fullName,
        user.slug,
        user.passwordCode,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = searchable.includes(search.toLowerCase());
      const matchesStatus = status === "all" || user.status === status;
      return matchesSearch && matchesStatus;
    });
  }, [search, status, safeUsers]);

  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return filteredUsers.slice(start, start + PER_PAGE);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PER_PAGE));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, filteredUsers.length]);

  const closeAll = () => {
    dispatch(closeModal());
    setActiveUser(null);
  };

  const openDeleteConfirm = (ids, single = false) => {
    dispatch(
      openModal({
        name: "confirmDelete",
        props: { ids, single },
      })
    );
  };

  const handleSubmit = (form, reset) => {
    const payload = {
      id: form.id || generateUserId(safeUsers),
      fullName: String(form.fullName || "").trim(),
      passwordCode: String(form.passwordCode || "").trim(),
      slug: String(form.slug || "").trim(),
      iframeCode: normalizeIframeMarkup(form.iframeCode),
      status: form.status === "inactive" ? "inactive" : "active",
    };

    if (!payload.fullName || !payload.passwordCode || !payload.iframeCode) {
      RenderToast({
        type: "error",
        message: "Full name, passcode, and iframe are required.",
      });
      return;
    }

    setLoading(true);
    saveUserRequest(payload)
      .then((result) => {
        const nextUser = normalizeUser({
          ...payload,
          id: result.id || payload.id,
          slug: result.slug || resolveUserSlug(payload),
          passwordCode: result.passwordCode || result.password_code || payload.passwordCode,
        });
        dispatch(upsertUser(nextUser));
        RenderToast({ type: "success", message: result.message || "User saved successfully" });
        setCurrentPage(1);
        closeAll();
        reset?.({
          id: "",
          fullName: "",
          passwordCode: "",
          slug: "",
          iframeCode: "",
          status: "active",
        });
      })
      .catch((error) => RenderToast({ type: "error", message: error.message || "Unable to save user" }))
      .finally(() => setLoading(false));
  };

  const handleDelete = () => {
    const ids = modalProps.ids || [];
    if (!ids.length) return;

    setLoading(true);
    deleteUsersRequest(ids)
      .then(() => {
        dispatch(removeUsers(ids));
        RenderToast({
          type: "success",
          message: ids.length > 1 ? "Users deleted" : "User deleted",
        });
        setSelected((prev) => prev.filter((id) => !ids.includes(id)));
        closeAll();
      })
      .catch((error) => RenderToast({ type: "error", message: error.message || "Unable to delete users" }))
      .finally(() => setLoading(false));
  };

  const toggleSelected = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const currentPageIds = useMemo(
    () => pagedUsers.map((user) => user.id),
    [pagedUsers],
  );

  const allCurrentPageSelected =
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selected.includes(id));

  const toggleAllCurrentPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allCurrentPageSelected) {
        currentPageIds.forEach((id) => next.delete(id));
      } else {
        currentPageIds.forEach((id) => next.add(id));
      }
      return Array.from(next);
    });
  };

  const columns = [
    {
      key: "select",
      label: "",
      headerRender: () => (
        <input
          type="checkbox"
          checked={allCurrentPageSelected}
          onChange={toggleAllCurrentPage}
          aria-label="Select all rows on this page"
        />
      ),
      render: (user) => (
        <input
          type="checkbox"
          checked={selected.includes(user.id)}
          onChange={() => toggleSelected(user.id)}
          aria-label={`Select ${user.fullName || user.id}`}
        />
      ),
    },
    {
      key: "serial",
      label: "S/N",
      render: (_user, index) => <strong>{(currentPage - 1) * PER_PAGE + index + 1}</strong>,
    },
    {
      key: "id",
      label: "ID",
      render: (user) => <div className={styles.cellScroll}><strong>{user.id}</strong></div>,
    },
    {
      key: "fullName",
      label: "Full Name",
      render: (user) => <div className={styles.cellScroll}><strong>{user.fullName || "-"}</strong></div>,
    },
    {
      key: "slug",
      label: "Slug",
      render: (user) => <div className={styles.cellScroll}>{user.slug || "-"}</div>,
    },
    {
      key: "status",
      label: "Status",
      render: (user) => (
        <span className={`${styles.status} ${styles[user.status]}`}>{user.status}</span>
      ),
    },
    {
      key: "action",
      label: "Action",
      render: (user) => (
        <div className={styles.rowActions}>
          <button type="button" onClick={() => setActiveUser({ ...user, viewOnly: true })} title="View">
            <FiEye />
          </button>
          <button type="button" onClick={() => setActiveUser(user)} title="Edit">
            <FiEdit2 />
          </button>
          <button
            type="button"
            onClick={() => openDeleteConfirm([user.id], true)}
            title="Delete"
          >
            <FiTrash2 />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.wrap}>
      <div className="pageCard" style={{ padding: "1.3rem" }}>
        <div className={styles.tableHeader}>
          <h2 className={styles.heading}>Users</h2>
          <div className={styles.headerControls}>
            <div className={styles.searchBox}>
              <FiSearch />
              <input
                className="inputBase"
                name="user_search"
                autoComplete="off"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by name, slug, or ID"
              />
            </div>
            <select
              className="selectBase"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <div className={styles.actions}>
              {selected.length > 0 ? (
                <button
                  type="button"
                  className="btnBase btnDanger"
                  onClick={() => openDeleteConfirm([...selected], false)}
                >
                  <FiTrash2 />
                  Delete Selected ({selected.length})
                </button>
              ) : null}
              <button
                type="button"
                className="btnBase btnPrimary"
                onClick={() =>
                  setActiveUser({
                    id: generateUserId(safeUsers),
                    fullName: "",
                    passwordCode: "",
                    slug: "",
                    iframeCode: "",
                    status: "active",
                    isNew: true,
                  })
                }
              >
                Add User
              </button>
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          rows={pagedUsers}
          loading={booting && users.length === 0}
          skeletonRows={PER_PAGE}
          emptyText="No users found."
          rowKey={(row) => row.id}
        />

        <div className={styles.tableFooter}>
          <span className={styles.totalRecord}>
            Total: {filteredUsers.length} record{filteredUsers.length !== 1 ? "s" : ""}
          </span>
          <PaginationComp
            total={filteredUsers.length}
            perPage={PER_PAGE}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <AddEditUser
        open={Boolean(activeUser && !activeUser.viewOnly)}
        user={activeUser && !activeUser.viewOnly ? activeUser : null}
        users={safeUsers}
        loading={loading}
        onClose={() => setActiveUser(null)}
        onSubmit={handleSubmit}
      />
      <ViewUser
        open={Boolean(activeUser && activeUser.viewOnly)}
        user={activeUser && activeUser.viewOnly ? activeUser : null}
        onClose={() => setActiveUser(null)}
      />
      <AreYouSure
        open={modal === "confirmDelete"}
        title={modalProps.ids?.length > 1 ? "Delete selected users?" : "Delete this user?"}
        message={
          modalProps.ids?.length > 1
            ? "This will remove all selected users."
            : "This user will be removed permanently."
        }
        confirmText={loading ? "Deleting..." : "Delete"}
        loading={loading}
        onClose={closeAll}
        onConfirm={handleDelete}
      />
    </div>
  );
}
